// Filet d'erreur React (DS §6.16, Task B2) : attrape ce qui échappe aux écrans
// — un écran qui CONSERVE (§6.11), jamais un cul-de-sac. Enveloppe `<Routes>`
// dans App.tsx : une erreur d'un écran ne blanchit donc jamais toute l'app,
// seulement la zone des routes (le header reste, lui, fonctionnel).

import { Component, type ErrorInfo, type ReactNode } from "react";
import { truncateError } from "../lib/signalement/fil";
import { AppHeader } from "./AppHeader";
import { PageShell } from "./PageShell";
import { SignalerProbleme } from "./SignalerProbleme";
import { Button } from "./ui/Button";
import { Card } from "./ui/Card";

const MESSAGE_GENERIQUE = "Quelque chose s'est mal passé. Ton travail en cours n'est pas perdu.";

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  erreur: Error | null;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  override state: AppErrorBoundaryState = { erreur: null };

  static getDerivedStateFromError(erreur: Error): AppErrorBoundaryState {
    return { erreur };
  }

  override componentDidCatch(erreur: Error, info: ErrorInfo): void {
    // Le motif technique part au journal (§6.13) — l'écran, lui, ne montre
    // jamais la pile brute.
    console.error("[AppErrorBoundary] erreur non rattrapée :", erreur, info.componentStack);
  }

  private reessayer = (): void => {
    this.setState({ erreur: null });
  };

  private allerAccueil = (): void => {
    window.location.assign("/");
  };

  override render(): ReactNode {
    const { erreur } = this.state;
    if (!erreur) return this.props.children;

    return (
      <PageShell>
        <AppHeader variant="plain" title="Écoute a rencontré un problème" />
        <div className="flex flex-col gap-3">
          <Card variant="danger">
            <p className="m-0 text-small">{MESSAGE_GENERIQUE}</p>
          </Card>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="primary" size="sm" onClick={this.reessayer}>
              Réessayer
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={this.allerAccueil}>
              Accueil
            </Button>
            <SignalerProbleme
              gravite="bloque"
              erreur={{
                message: erreur.message,
                pile: erreur.stack ? truncateError(erreur.stack, 4000) : null,
              }}
              ouvrirAuMontage={false}
            />
          </div>
        </div>
      </PageShell>
    );
  }
}
